import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ModelosRemuneracao } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { AgFilterUtils, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import { ModelosRemuneracaoService } from './modelos-remuneracao.service';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'ANALISES.MODELOS_DE_REMUNERACAO';

const COLUMNS: ColDef[] = [
  {
    headerName: 'Competência',
    field: 'nr_competencia',
    type: 'competencia',
    width: 140,
  },
  {
    headerName: 'Tipo de rede',
    field: 'nm_tp_rede',
    width: 160,
  },
  {
    headerName: 'Origem da guia',
    field: 'nm_id_origem_guia',
    width: 210,
  },
  {
    headerName: 'Tipo de guia',
    field: 'nm_tp_guia',
    width: 180,
  },
  {
    headerName: 'Modelo de remuneração',
    field: 'nm_id_modelo_remuneracao',
    width: 240,
  },
  {
    headerName: 'Quantidade de guias',
    field: 'qt_guia',
    type: 'int',
    width: 160,
  },
  {
    headerName: 'Valor informado',
    field: 'vl_informado',
    type: 'money',
    width: 160,
  },
];

@Component({
    templateUrl: './modelos-remuneracao.component.html',
    styleUrls: ['./modelos-remuneracao.component.scss'],
    providers: [ModelosRemuneracaoService, DecimalPipe],
    standalone: false
})
export class ModelosRemuneracaoComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;

  gridOptions: GridOptions<Row>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];

  rowData: Row[];

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: ModelosRemuneracaoService,
    private geralService: GeralService,
    private decimalPipe: DecimalPipe,
    private competenciaPipe: CompetenciaPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<Row>({
      columnDefs: COLUMNS,
      pipes: {
        decimal: this.decimalPipe,
        competencia: this.competenciaPipe,
      },
      views: {
        gridKey: GRID_KEY,
        hide: !this.podeGerenciarVisoes,
      },
      exportExcelFn: () => this.exportExcel(),
    });
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    this.filterChanged = false;

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.competenciaRange[0] || !this.competenciaRange[1]) {
      this.rowData = [];
      this.agGrid?.api?.setGridOption('loading', false);
      return;
    }

    this.service.getData(this.operadora, this.competenciaRange[0], this.competenciaRange[1]).subscribe({
      next: items => {
        this.rowData = (items ?? []).map(item => ({
          ...item,
          nm_tp_rede: this.geralService.buildNomeDeDominio(item.tp_rede, 'MON_TIPO_REDE'),
          nm_id_origem_guia: this.geralService.buildNomeDeDominio(item.id_origem_guia, 'MON_ORIGEM_GUIA'),
          nm_tp_guia: this.geralService.buildNomeDeDominio(item.tp_guia, 'MON_TIPO_GUIA'),
          nm_id_modelo_remuneracao: this.geralService.buildNomeDeDominio(item.id_modelo_remuneracao, 'dm_formaRemuneracao'),
        }));
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  onSearch(value: string) {
    this.agGrid?.api?.updateGridOptions({ quickFilterText: value });
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.filterChanged = true;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel(): void {
    const fileNamePrefix = `modelos-de-remuneracao-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const headers = [
      'Modelos de remuneração',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

interface Row extends ModelosRemuneracao {
  nm_tp_rede: string;
  nm_id_origem_guia: string;
  nm_tp_guia: string;
  nm_id_modelo_remuneracao: string;
}
